import { showToast } from "@certn/cascadia/components/toast/utils";
import { FormikErrors, FormikHelpers } from "formik";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { APIError, defaultParseError, defaultParseFieldErrors } from "./errors";
import { UseSubmitOptions, useSubmit } from "./useSubmit";

interface UseFormikSubmitOptions<Response> extends UseSubmitOptions<Response> {
  setStatusOnError?: boolean;
  parseError?: (error: APIError) => string;
  parseFieldErrors?: (error: APIError) => Record<string, unknown>;
  resetOnSuccess?: boolean;
}

export function useFormikSubmit<Response extends unknown>(
  path: string,
  options: UseFormikSubmitOptions<Response> = {}
) {
  const { t } = useTranslation();

  const {
    setStatusOnError,
    resetOnSuccess,
    parseError = defaultParseError,
    parseFieldErrors = defaultParseFieldErrors,
    ...useSubmitOptions
  } = options;

  const submitter = useSubmit(path, {
    ...useSubmitOptions,
    showToastOnError: false, // we'll handle this instead
    throwErrors: true,
  });

  const submit = useCallback(
    async <T, H>(values: T, helpers: FormikHelpers<H>) => {
      helpers.setErrors({});

      if (options.setStatusOnError) {
        helpers.setStatus();
      }

      try {
        await submitter.submit(values);
        if (options.resetOnSuccess) {
          helpers.resetForm();
        }
      } catch (e) {
        let status = t("Something went wrong");

        if (e instanceof APIError) {
          status = parseError(e) || status;

          const errors = parseFieldErrors(e);
          helpers.setErrors(errors as FormikErrors<H>);
        }

        if (options.showToastOnError) {
          showToast(
            typeof options.showToastOnError === "string"
              ? options.showToastOnError
              : status,
            {
              type: "error",
            }
          );
        }

        if (options.setStatusOnError) {
          helpers.setStatus(status);
        }
      }
    },
    [
      parseFieldErrors,
      parseError,
      options.showToastOnError,
      options.setStatusOnError,
      path,
    ]
  );

  return { ...submitter, submit };
}
